import type { ReactNode } from 'react';
import { MEDIA_FORMAT_LABELS } from '@ooh/contracts';
import { Image, ImageOff } from 'lucide-react';
import { INTERACTIVE_CELL_CLASSNAME } from '@ooh/ui/components/data-table';
import { Button } from '@ooh/ui/components/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ooh/ui/components/tooltip';

export const MEDIA_UNIT_EMPTY_VALUE = '—';

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function nonEmptyText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function renderMediaUnitText(value: unknown, className = 'text-xs'): ReactNode {
  const text = nonEmptyText(value);
  return <span className={className}>{text ?? MEDIA_UNIT_EMPTY_VALUE}</span>;
}

export function renderMediaUnitGrwId(value: unknown): ReactNode {
  return renderMediaUnitText(value, 'font-mono text-xs');
}

export function renderMediaUnitNoPhoto(hasNoPhoto: boolean, ariaLabel: string): ReactNode {
  return hasNoPhoto ? (
    <ImageOff
      className="text-muted-foreground inline-block size-4"
      aria-label={ariaLabel}
      data-testid="no-photo-indicator"
    />
  ) : null;
}

/**
 * Photo-status indicator for the catalog table's `noPhoto` column. No-photo rows
 * keep the existing static `ImageOff` icon (delegates to `renderMediaUnitNoPhoto`
 * to avoid duplicating that render). Has-photo rows render a small clickable,
 * keyboard-accessible thumbnail of the unit's first photo that opens the
 * full-screen photo preview via `onOpenPreview` — disabled while the preview's
 * photo data is still loading. A `Tooltip` shows a larger version of the same
 * photo on hover, using the route's ancestor `TooltipProvider`. The button is
 * always wrapped in the same `Tooltip`/`TooltipTrigger` tree shape regardless
 * of `isLoading` — conditionally adding/removing that wrapper would change the
 * element type at this position on every loading-state transition, forcing
 * React to unmount and remount the button (losing focus and detaching any
 * already-captured DOM reference, e.g. in tests holding onto the indicator
 * across a click-then-reload cycle).
 *
 * Carries `INTERACTIVE_CELL_CLASSNAME` (`relative z-[1]`) and stops click
 * propagation, same as the catalog table's row-selection checkbox cell — both
 * are needed to win the hit-test over the row's stretched-link navigation
 * overlay (see `data-table-helpers.tsx`'s row-link z-index scheme); without it
 * a click here would navigate to the unit detail page instead of opening the
 * preview.
 */
export function renderMediaUnitPhotoStatus(
  photoUrl: string | null,
  labels: { noPhotoAria: string; hasPhotoAria: string },
  onOpenPreview: () => void,
  isLoading = false,
): ReactNode {
  if (photoUrl === null) return renderMediaUnitNoPhoto(true, labels.noPhotoAria);

  const button = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={(event) => {
        event.stopPropagation();
        onOpenPreview();
      }}
      disabled={isLoading}
      aria-label={labels.hasPhotoAria}
      data-testid="has-photo-indicator"
      className={`cursor-pointer p-0.5 ${INTERACTIVE_CELL_CLASSNAME}`}
    >
      <img
        src={photoUrl}
        alt=""
        loading="lazy"
        className="size-7 min-h-0 min-w-0 rounded-sm object-cover"
      />
    </Button>
  );

  // A disabled native element never fires the pointer/focus events a Radix
  // tooltip trigger depends on (disabled -> pointer-events: none), so the
  // tooltip trigger wraps a span instead of the button directly — same fix
  // as DisabledHintTooltip (packages/ui/src/components/disabled-hint-tooltip.tsx).
  // The span is ALWAYS present (not conditionally added only while isLoading):
  // toggling the wrapper in/out would change the element type at this tree
  // position on every loading-state transition, forcing React to unmount and
  // remount the button (see this function's doc comment above re: keeping one
  // constant Tooltip/TooltipTrigger tree shape — confirmed by re-breaking
  // -media-units-photo-preview.test.tsx's retry-after-failure case when tried).
  // Only `tabIndex` toggles: `0` while loading makes the span itself a real
  // keyboard tab stop (the disabled button is removed from the tab order), and
  // `-1` while not loading keeps the wrapper out of the tab order so the
  // enabled button remains the sole tab stop. Pointer-hover works either way
  // since pointer events bubble from the (possibly pointer-events:none) button
  // up to this span regardless of tabIndex.
  const trigger = <span tabIndex={isLoading ? 0 : -1}>{button}</span>;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent className="border border-border bg-popover p-1 text-popover-foreground shadow-md">
        <img src={photoUrl} alt="" className="max-h-44 max-w-44 rounded-sm object-contain" />
      </TooltipContent>
    </Tooltip>
  );
}

export function renderMediaUnitNoPhotoHeader(ariaLabel: string): ReactNode {
  return (
    <Image
      role="img"
      className="text-muted-foreground inline-block size-4"
      aria-label={ariaLabel}
      data-testid="no-photo-column-header-icon"
    />
  );
}

export function renderMediaUnitFormat(formatCode: unknown, formatLabel: unknown): ReactNode {
  const code = nonEmptyText(formatCode);
  const label = nonEmptyText(formatLabel);
  const displayValue = (code == null ? null : MEDIA_FORMAT_LABELS[code]) ?? label ?? code;
  return renderMediaUnitText(displayValue);
}

export function renderMediaUnitAudience(value: unknown): ReactNode {
  const audience = finiteNumber(value);
  return (
    <span className="font-mono text-xs">
      {audience == null
        ? MEDIA_UNIT_EMPTY_VALUE
        : audience.toLocaleString('pl-PL', { maximumFractionDigits: 0 })}
    </span>
  );
}

export function renderMediaUnitDimensions(
  width: unknown,
  height: unknown,
  notes: unknown,
): ReactNode {
  const widthM = finiteNumber(width);
  const heightM = finiteNumber(height);
  const dimensionNotes = nonEmptyText(notes);
  const numericPart =
    widthM != null && heightM != null
      ? `${widthM} × ${heightM} m`
      : widthM != null
        ? `${widthM} m`
        : heightM != null
          ? `${heightM} m`
          : null;

  if (numericPart == null && dimensionNotes == null) {
    return <span className="font-mono text-xs">{MEDIA_UNIT_EMPTY_VALUE}</span>;
  }
  if (numericPart == null) return <span className="text-xs">{dimensionNotes}</span>;
  if (dimensionNotes == null) return <span className="font-mono text-xs">{numericPart}</span>;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-mono text-xs">{numericPart}</span>
      <span className="text-2xs text-muted-foreground">{dimensionNotes}</span>
    </div>
  );
}

export function renderMediaUnitArea(value: unknown): ReactNode {
  const area = finiteNumber(value);
  return (
    <span className="font-mono text-xs">
      {area == null
        ? MEDIA_UNIT_EMPTY_VALUE
        : `${area.toLocaleString('pl-PL', { maximumFractionDigits: 2 })} m²`}
    </span>
  );
}

export function renderMediaUnitBoolean(
  value: unknown,
  labels: { trueLabel: string; falseLabel: string },
): ReactNode {
  if (typeof value !== 'boolean') return renderMediaUnitText(null);
  return (
    <span className={value ? 'font-medium text-emerald-600' : 'text-muted-foreground'}>
      {value ? labels.trueLabel : labels.falseLabel}
    </span>
  );
}

export function renderMediaUnitTags(value: unknown): ReactNode {
  if (!Array.isArray(value)) return renderMediaUnitText(null);
  const tags: unknown[] = value;
  const labels = tags.flatMap((tag) => {
    if (typeof tag === 'string') return nonEmptyText(tag) ?? [];
    if (typeof tag !== 'object' || tag == null || !('label' in tag)) return [];
    return nonEmptyText(tag.label) ?? [];
  });
  return renderMediaUnitText(labels.length > 0 ? labels.join(', ') : null);
}
